import { AlertUrgency, AlertType } from "@/enums/Alerts";
import { AlertResponse, Alert, LeaId } from "@/models/Alerts";

const ALERT_SETTINGS = {
  [AlertType.ReassessStudent]: {
    title: false,
    description: false,
  },
  [AlertType.ReviewTestScoring]: {
    title: false,
    description: false,
  },
  [AlertType.StudentBadgeEarned]: {
    title: false,
    description: false,
  },
  [AlertType.ReadingRiskFlagged]: {
    title: false,
    description: false,
  },
  [AlertType.StudentNotReadingOrEngaging]: {
    title: false,
    description: false,
  },
  [AlertType.AssessmentHasReviewNeededYellowFlagging]: {
    title: false,
    description: false,
  },
  [AlertType.SkillGapNeedsAttention]: {
    title: false,
    description: false,
  },
  [AlertType.StudentSkillGapFilled]: {
    title: false,
    description: false,
  },
  [AlertType.LessonPlanFixNeeded]: {
    title: false,
    description: false,
  },
  [AlertType.AssignmentRecommended]: {
    title: false,
    description: false,
  },
  [AlertType.LessonPlanAutoFix]: {
    title: false,
    description: false,
  },
};

// Initial sort is by attentionLevel, then if the same attentionLevel,
// sort by the order below (Left to Right and Top to Bottom - Widget Order)
const SORTED_ALERT_TYPES = [
  // Assessment status widget
  AlertType.ReassessStudent,
  AlertType.ReviewTestScoring,
  AlertType.AssessmentHasReviewNeededYellowFlagging,

  // Assessment outcomes widget
  AlertType.ReadingRiskFlagged,

  // Instruct widget
  AlertType.SkillGapNeedsAttention,
  AlertType.LessonPlanFixNeeded,
  AlertType.AssignmentRecommended,
  AlertType.StudentSkillGapFilled,

  // Tutor widget
  AlertType.StudentNotReadingOrEngaging,
  AlertType.StudentBadgeEarned,
];

export class AlertsService {
  public parseAlerts(alerts: AlertResponse[]): Alert[] {
    return (
      alerts
        // Filter out alerts that are not in the AlertType enum
        // In this case the UI will know how to handle the alert
        .filter(alert =>
          Object.values(AlertType).includes(
            alert.notificationTypes.name as AlertType
          )
        )
        .map(alert => {
          let leaId: LeaId = {
            contentType: "",
            attentionLevel: "",
            classroomId: 0,
            districtId: 0,
            alertVO: "",
          };
          try {
            leaId = JSON.parse(alert.leaId as unknown as string) as LeaId;
          } catch (error) {
            console.error("Failed to parse leaId:", error);
          }
          // TODO: parse message and title if some types of alerts need it
            const { 
            leaId: _, 
            notificationTypes: { name: type, notificationTypeDescription: description }, 
            product: { name: productName },
            title: rawTitle,
            message,
            ...alertRest 
            } = alert;

            const newAlert = {
            ...alertRest,
            ...leaId,
            productName,
            type,
            ...(ALERT_SETTINGS[type as AlertType].description && { message }),
            ...(ALERT_SETTINGS[type as AlertType].title && { title: rawTitle }),
            ...(leaId.studentId && { studentId: [leaId.studentId] }),
            description,
            groupedAlertIds: [alert.id],
            } as Alert;
          return newAlert as Alert;
        })
    );
  }

  public processAlerts(alerts: Alert[]): {
    [classroomId: string]: Alert[];
  } {
    // Group alerts by classroomId
    const groupedByClassroom: {
      [classroomId: string]: Alert[];
    } = {};

    alerts.forEach(alert => {
      const classroomId = alert.classroomId || "unknown";

      if (!groupedByClassroom[classroomId]) {
        groupedByClassroom[classroomId] = [];
      }
      groupedByClassroom[classroomId].push(alert);
    });

    // Process each classroom's alerts
    const result: { [classroomId: string]: Alert[] } = {};

    Object.entries(groupedByClassroom).forEach(
      ([classroomId, classroomAlerts]) => {
        const categorized = {
          [AlertUrgency.Urgent]: [] as Alert[],
          [AlertUrgency.Rare]: [] as Alert[],
          [AlertUrgency.Common]: [] as Alert[],
        };

        // Group alerts with the same type and merge their studentIds
        const mergedAlerts = classroomAlerts.reduce(
          (acc: Alert[], curr: Alert) => {
            const existingAlert = acc.find(a => a.type === curr.type);
            if (existingAlert && curr.studentId) {
              existingAlert.studentId = [
                ...new Set([
                  ...(existingAlert.studentId || []),
                  ...curr.studentId,
                ]),
              ];
              existingAlert.groupedAlertIds = [
                ...(existingAlert.groupedAlertIds || []),
                curr.id,
              ];
              return acc;
            }
            acc.push(curr);
            return acc;
          },
          []
        );

        // Categorize alerts
        mergedAlerts.forEach(alert => {
          const attentionLevel = alert.attentionLevel || AlertUrgency.Common;
          categorized[attentionLevel as AlertUrgency].push({ ...alert });
        });

        const sortByAlertType = (a: Alert, b: Alert) => {
          const typeA = SORTED_ALERT_TYPES.indexOf(a.type as AlertType);
          const typeB = SORTED_ALERT_TYPES.indexOf(b.type as AlertType);
          return typeA - typeB;
        };

        const urgentSorted = categorized.urgent.sort(sortByAlertType);
        const rareSorted = categorized.rare.sort(sortByAlertType);
        const commonSorted = categorized.common.sort(sortByAlertType);

        result[classroomId] = [...urgentSorted, ...rareSorted, ...commonSorted];
      }
    );

    return result;
  }
}

export default new AlertsService();
